import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { api } from "../../lib/api";
import { Plus, Search as SearchIcon, MoreVertical, ChevronDown, X } from "lucide-react";
import { SERVICES } from "../../data/staticData";
import { useAuth } from "../../lib/auth";

const STATUS_LABEL = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  in_review: { label: "In review", cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In progress", cls: "bg-indigo-100 text-indigo-800" },
  delivered: { label: "Complete", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
};

const STATUS_FILTERS = ["all", "pending", "in_review", "in_progress", "delivered", "cancelled"];

function initialsOf(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "U";
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = d.getDate();
  const suffix = ["th", "st", "nd", "rd"][((day % 100) - 20) % 10] || ["th", "st", "nd", "rd"][day] || "th";
  return `${day}${suffix} ${d.toLocaleString("en-US", { month: "long" })} ${d.getFullYear()}`;
}

function dueDate(iso) {
  if (!iso) return "Not Assigned";
  const d = new Date(iso);
  d.setDate(d.getDate() + 14);
  return formatDate(d.toISOString());
}

function serviceLabel(slug) {
  const s = SERVICES.find((x) => x.slug === slug);
  return s?.title || slug;
}

const MORNING_GREETING = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterService, setFilterService] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [tagChips, setTagChips] = useState(["SE2", "SE15", "SE13", "SE16"]);
  const [openMenu, setOpenMenu] = useState(null);
  const [limit, setLimit] = useState(15);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/orders");
      if (data.length === 0) {
        try { /* seed-demo removed for live users */ } catch (_) {}
        const { data: d2 } = await api.get("/orders");
        setOrders(d2);
      } else {
        setOrders(data);
      }
      setLoading(false);
    })();
  }, []);

  const visible = useMemo(() => {
    return orders.filter((o) => {
      if (filterService !== "all" && o.service_slug !== filterService) return false;
      if (filterStatus !== "all" && o.status !== filterStatus) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = `${o.order_id} ${o.service_slug} ${o.package_name || ""} ${JSON.stringify(o.requirements || {})}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, filterService, filterStatus, query]);

  const removeChip = (c) => setTagChips((arr) => arr.filter((x) => x !== c));

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{MORNING_GREETING()}, {user?.name?.split(" ")[0] || "there"}!</h1>
          <p className="text-inkSoft mt-1">Let's see how your orders are getting on!</p>
        </div>
        <div className="flex flex-col items-stretch lg:items-end gap-2">
          <Link to="/services" className="btn-ghost text-sm self-end" data-testid="orders-explore-services">Explore services</Link>
          <div className="text-right">
            <div className="text-sm">Credit balance: <span className="font-mono font-semibold">0.00</span> <span className="text-inkSoft">(£0.00 | $0.00)</span></div>
            <div className="text-xs mt-1 text-terra"><Link to="/contact" className="hover:underline">Add Credits</Link> · <Link to="/contact" className="hover:underline">View Credit Invoices</Link></div>
          </div>
        </div>
      </div>

      {/* Tag chips */}
      {tagChips.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="tag-chips">
          {tagChips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 bg-white border border-line rounded-full px-3 py-1.5 text-xs font-mono">
              {c}
              <button onClick={() => removeChip(c)} className="text-inkSoft hover:text-ink" data-testid={`chip-remove-${c}`}><X size={12} /></button>
            </span>
          ))}
          <button className="inline-flex items-center gap-1 text-xs text-inkSoft px-3 py-1.5 rounded-full border border-line"><span>More</span> <ChevronDown size={12} /></button>
        </div>
      )}

      {/* Search + filters */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_220px_220px] gap-3" data-testid="orders-filters">
        <label className="relative">
          <SearchIcon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkSoft" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by order number, domain, or keyword..."
            className="w-full h-11 pl-10 pr-4 rounded-full bg-white border border-line text-sm outline-none focus:border-ink"
            data-testid="orders-search"
          />
        </label>
        <select
          value={filterService}
          onChange={(e) => setFilterService(e.target.value)}
          className="h-11 rounded-full border border-line bg-white px-4 text-sm outline-none focus:border-ink"
          data-testid="orders-service-filter"
        >
          <option value="all">All Services</option>
          {SERVICES.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-11 rounded-full border border-line bg-white px-4 text-sm outline-none focus:border-ink"
          data-testid="orders-status-filter"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All Orders" : STATUS_LABEL[s]?.label || s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-line bg-white overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-inkSoft">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="px-6 py-12 text-sm text-inkSoft text-center">
            No orders yet. <Link to="/dashboard/new-order" className="text-terra hover:underline">Place your first order</Link>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-inkSoft border-b border-line bg-surfaceAlt/40">
                  <th className="px-6 py-4 font-medium">Service</th>
                  <th className="px-6 py-4 font-medium">Ordered by</th>
                  <th className="px-6 py-4 font-medium">Order number</th>
                  <th className="px-6 py-4 font-medium">Date ordered</th>
                  <th className="px-6 py-4 font-medium">Date due</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Tags</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.slice(0, limit).map((o) => {
                  const status = STATUS_LABEL[o.status] || { label: o.status, cls: "bg-gray-100 text-gray-800" };
                  return (
                    <tr key={o.order_id} className="border-b border-line last:border-0 hover:bg-surfaceAlt/30 transition" data-testid={`order-row-${o.order_id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium">{serviceLabel(o.service_slug)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-8 w-8 rounded-full bg-ink text-white grid place-items-center text-xs font-semibold" title={user?.name}>{initialsOf(user?.name)}</div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs">FJ{o.order_id.replace(/[^0-9a-f]/gi, "").slice(0, 10).toUpperCase()}</td>
                      <td className="px-6 py-4 text-inkSoft">{formatDate(o.created_at)}</td>
                      <td className="px-6 py-4 text-inkSoft">{o.status === "pending" ? "Not Assigned" : dueDate(o.created_at)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${status.cls}`} data-testid={`status-${o.order_id}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button className="inline-flex items-center gap-1 text-xs text-inkSoft hover:text-ink">
                          <span className="opacity-60">Add Tag…</span>
                        </button>
                      </td>
                      <td className="px-6 py-4 relative">
                        <button
                          onClick={() => setOpenMenu(openMenu === o.order_id ? null : o.order_id)}
                          className="h-8 w-8 grid place-items-center rounded-lg hover:bg-surfaceAlt"
                          data-testid={`order-menu-${o.order_id}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {openMenu === o.order_id && (
                          <div className="absolute right-6 top-12 z-10 w-44 bg-white rounded-xl border border-line shadow-[0_20px_60px_rgba(0,0,0,0.12)] py-1 text-sm">
                            <Link to={`/dashboard/orders/${o.order_id}`} className="block px-4 py-2 hover:bg-surfaceAlt">View order</Link>
                            <Link to="/dashboard/new-order" className="block px-4 py-2 hover:bg-surfaceAlt">Order again</Link>
                            <button className="w-full text-left px-4 py-2 hover:bg-surfaceAlt">View invoice</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-surfaceAlt">CSV download</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {visible.length > 0 && (
        <div className="mt-6 flex flex-col items-center gap-3">
          <div className="text-xs text-inkSoft font-mono">Showing {Math.min(limit, visible.length)} of {visible.length} orders</div>
          {visible.length > limit && (
            <button onClick={() => setLimit((l) => l + 15)} className="btn-ghost text-sm" data-testid="load-more">Load More</button>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
